import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { can, useAuth } from '../lib/auth';
import { isFormula, readNumber } from '../lib/formula';

/**
 * Reconcile board against jobs.  Paper is issued to a job card from the
 * Stock tab; when production uses more than was issued, the extra sheets are
 * booked here against the job with a reason (stock goes down), and sheets
 * left over go back to stock with a reason (stock goes up).  Searching a job
 * number shows everything issued to it, per board and movement by movement.
 */

interface JobRef {
  jobId: string; clientName: string; productName: string; productId: string;
  poNumber: string; poQty: number; jobCardQty: number; cutSize: string; sheetsPlanned: number;
}
interface BoardTotal {
  boardId: number; sku: string; paperType: string; lengthIn: number | null; widthIn: number | null; gsm: number | null;
  sheetsOnHand: number; issued: number; extra: number; returned: number; netUsed: number;
}
interface JobTxn {
  id: number; boardId: number; type: string; effectiveType: string; sheets: number; rate: number | null;
  reason: string; notes: string; createdOn: string; byName: string;
  sku: string; paperType: string; lengthIn: number | null; widthIn: number | null; gsm: number | null;
  reversesTxnId: number | null; reversedByTxnId: number | null;
  /** This user may reverse it now (Purchase: within an hour of the movement). */
  canReverse?: boolean;
}
interface JobView {
  found: boolean; job: JobRef | null; boards: BoardTotal[]; txns: JobTxn[];
  /** Reconcile is open for RECONCILE_DAYS from the job's first board movement. */
  reconcile?: { open: boolean; firstOn: string | null; closesOn: string | null };
}

/** Days a job can be reconciled from its first board movement (the API decides). */
const RECONCILE_DAYS = 30;
const day = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
interface RecentJob {
  jobId: string; lastOn: string; clientName: string; productName: string;
  issued: number; extra: number; returned: number; netUsed: number;
}
interface BoardLite {
  id: number; sku: string; paperType: string; lengthIn: number | null; widthIn: number | null; gsm: number | null; sheetsOnHand: number;
}

type Kind = 'extra' | 'return';

const EXTRA_REASONS = [
  'Higher printing wastage', 'Machine setting / make-ready', 'Reprint — print defect',
  'Cutting / die-cutting wastage', 'Board damaged', 'Short issued earlier', 'Other',
];
const RETURN_REASONS = ['Leftover after job', 'Over-issued', 'Job quantity reduced', 'Job cancelled / on hold', 'Other'];

/** A failed Reverse, as the store should read it. */
export function reverseError(e: unknown): string {
  const m = (e as Error).message;
  if (m === 'reverse_window_passed') return 'More than 1 hour has passed since this movement — only Super Admin can reverse it now.';
  return `Reverse failed: ${m}`;
}

/**
 * A movement's notes, each added note on its own line, and (for whoever may
 * reconcile) "+ Note" to add another - kept with the earlier ones, dated and
 * named, never replacing them.
 */
function NoteCell({ txnId, notes, canAdd, onSaved }: { txnId: number; notes: string; canAdd: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/api/paper-stock/txns/${txnId}/note`, { method: 'POST', body: JSON.stringify({ note: text.trim() }) }),
    onSuccess: () => { setText(''); setOpen(false); onSaved(); },
  });
  const parts = (notes || '').split(' | ').filter(Boolean);
  const errMsg = (() => {
    const m = (save.error as Error | null)?.message ?? '';
    if (!m) return '';
    if (m.startsWith('notes_full')) return 'The notes on this row are full.';
    if (m === 'forbidden') return 'You cannot add notes here.';
    return m;
  })();
  return (
    <div>
      {parts.length ? parts.map((p, i) => <div key={i}>{p}</div>) : <span>—</span>}
      {canAdd && !open && (
        <button type="button" className="mt-0.5 text-[11px] font-medium text-sky-700 hover:underline" onClick={() => setOpen(true)}>+ Note</button>
      )}
      {open && (
        <form className="mt-1 flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); if (text.trim() && !save.isPending) save.mutate(); }}>
          <input className="input !py-1 !text-xs" autoFocus maxLength={300} value={text} placeholder="Add a note"
                 onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }} />
          <button type="submit" className="btn-primary !py-1 !px-2 text-[11px]" disabled={!text.trim() || save.isPending}>{save.isPending ? '…' : 'Save'}</button>
          <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => { setOpen(false); setText(''); }}>Cancel</button>
        </form>
      )}
      {errMsg && <div className="text-[11px] text-red-600">{errMsg}</div>}
    </div>
  );
}

export const TXN_LABEL: Record<string, string> = {
  ISSUE: 'Issued', RECON_EXTRA: 'Extra used', RECON_RETURN: 'Returned',
  RECEIVE: 'Received', ADJUST: 'Adjusted', REVERSAL: 'Reversal',
};

const n = (v: number) => v.toLocaleString('en-IN');
const when = (s: string) => new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const boardText = (b: { sku: string; paperType: string; lengthIn: number | null; widthIn: number | null; gsm: number | null }) =>
  [b.sku, b.paperType, b.lengthIn != null && b.widthIn != null ? `${b.lengthIn} × ${b.widthIn}` : '', b.gsm ? `${b.gsm} GSM` : '']
    .filter(Boolean).join(' · ');

export function JobReconcileTab({ onChanged }: { onChanged: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Accounts and GM look jobs up; booking and reversing are Purchase's.
  const canManage = can(user, 'MANAGE_INVENTORY');
  const [input, setInput] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [action, setAction] = useState<{ kind: Kind; boardId: number | null } | null>(null);

  const recent = useQuery({
    queryKey: ['board-job-recent'],
    queryFn: () => api<{ rows: RecentJob[] }>('/api/paper-stock/jobs/recent'),
  });
  const view = useQuery({
    queryKey: ['board-job', open],
    queryFn: () => api<JobView>(`/api/paper-stock/job/${encodeURIComponent(open!)}`),
    enabled: !!open,
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['board-job'] });
    qc.invalidateQueries({ queryKey: ['board-job-recent'] });
    onChanged();
  };

  const reverse = useMutation({
    mutationFn: (txnId: number) => api(`/api/paper-stock/txns/${txnId}/reverse`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: refreshAll,
    onError: (e) => alert(reverseError(e)),
  });

  function find(e?: FormEvent) {
    e?.preventDefault();
    const v = input.trim();
    if (v) setOpen(v);
  }

  const term = input.trim().toLowerCase();
  const recentRows = (recent.data?.rows ?? []).filter(r =>
    !term || [r.jobId, r.clientName, r.productName].some(x => x.toLowerCase().includes(term)));

  const v = view.data;
  const job = v?.job ?? null;
  const tot = (v?.boards ?? []).reduce(
    (s, b) => ({ issued: s.issued + b.issued, extra: s.extra + b.extra, returned: s.returned + b.returned, netUsed: s.netUsed + b.netUsed }),
    { issued: 0, extra: 0, returned: 0, netUsed: 0 },
  );
  const planned = job?.sheetsPlanned ?? 0;
  const vsPlan = planned > 0 ? tot.netUsed - planned : null;
  // Extra used / returned can be booked only while the job's window is open.
  const reconcileOpen = !!job && (v?.reconcile?.open ?? true);
  const canReconcile = canManage && reconcileOpen;

  return (
    <div>
      <form onSubmit={find} className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex-1 min-w-[260px]">
          <label className="label">Job card number</label>
          <input className="input" value={input} onChange={e => setInput(e.target.value)} autoFocus
            placeholder="e.g. 81852 or VP/092026/81852 — or pick from the list below" />
        </div>
        <button type="submit" className="btn-primary" disabled={!input.trim()}>Find job</button>
        {open && <button type="button" className="btn-ghost" onClick={() => setOpen(null)}>← All jobs</button>}
      </form>

      {!open ? (
        <>
          <p className="text-xs text-slate-500 mb-2">
            Jobs that have had board issued, latest first. Open one to see everything issued to it, and to book extra sheets
            production used or sheets returned — each with a reason.
          </p>
          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Job</th>
                  <th className="px-3 py-2 text-left font-medium">Client</th>
                  <th className="px-3 py-2 text-left font-medium">Product</th>
                  <th className="px-3 py-2 text-right font-medium">Issued</th>
                  <th className="px-3 py-2 text-right font-medium">Extra used</th>
                  <th className="px-3 py-2 text-right font-medium">Returned</th>
                  <th className="px-3 py-2 text-right font-medium">Net used</th>
                  <th className="px-3 py-2 text-left font-medium">Last movement</th>
                </tr>
              </thead>
              <tbody>
                {recent.isLoading ? (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-400">Loading…</td></tr>
                ) : recentRows.length === 0 ? (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-400">
                    {term ? 'No job in this list matches — press Find job to look it up.' : 'No board has been issued to a job yet.'}
                  </td></tr>
                ) : recentRows.map(r => (
                  <tr key={r.jobId} className="border-t border-slate-100 hover:bg-sky-50 cursor-pointer"
                    onClick={() => { setInput(r.jobId); setOpen(r.jobId); }}>
                    <td className="px-3 py-1.5 font-mono font-semibold text-brand">{r.jobId}</td>
                    <td className="px-3 py-1.5">{r.clientName || '—'}</td>
                    <td className="px-3 py-1.5 max-w-[240px] truncate" title={r.productName}>{r.productName || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{n(r.issued)}</td>
                    <td className={`px-3 py-1.5 text-right ${r.extra ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{r.extra ? n(r.extra) : '—'}</td>
                    <td className={`px-3 py-1.5 text-right ${r.returned ? 'text-emerald-700 font-semibold' : 'text-slate-400'}`}>{r.returned ? n(r.returned) : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{n(r.netUsed)}</td>
                    <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{when(r.lastOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : view.isLoading ? (
        <div className="bg-white rounded shadow p-6 text-center text-slate-400">Loading…</div>
      ) : view.isError || !v ? (
        <div className="bg-white rounded shadow p-6 text-center text-red-600">Couldn't load that job: {(view.error as Error)?.message}</div>
      ) : !v.found && v.txns.length === 0 ? (
        <div className="bg-white rounded shadow p-6 text-center text-slate-500">No job card “{open}”, and nothing has been issued to it.</div>
      ) : (
        <div className="space-y-4">
          {/* Job */}
          <div className="bg-white rounded shadow p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-mono text-base font-semibold text-brand">{job?.jobId ?? open}</div>
                {job ? (
                  <div className="text-xs text-slate-600 mt-0.5">
                    {job.clientName || '—'} · {job.productName || '—'}
                    {job.poNumber && <> · PO {job.poNumber}{job.poQty ? ` (${n(job.poQty)})` : ''}</>}
                    {job.jobCardQty > 0 && <> · JC qty {n(job.jobCardQty)}</>}
                    {job.cutSize && <> · Cut {job.cutSize}</>}
                  </div>
                ) : (
                  <div className="text-xs text-amber-700 mt-0.5">This job card isn't in Job Orders any more — its board movements are still here.</div>
                )}
                {v.reconcile?.closesOn && (
                  v.reconcile.open ? (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Reconcile open until <b>{day(v.reconcile.closesOn)}</b> — {RECONCILE_DAYS} days from the first issue on {day(v.reconcile.firstOn!)}.
                    </div>
                  ) : (
                    <div className="mt-1 inline-block rounded border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                      Reconcile closed on {day(v.reconcile.closesOn)} — {RECONCILE_DAYS} days after the first issue. What this job left in stock is final.
                    </div>
                  )
                )}
              </div>
              {canReconcile && (
                <div className="flex gap-2">
                  <button className="btn-ghost text-xs" onClick={() => setAction({ kind: 'extra', boardId: v.boards[0]?.boardId ?? null })}>+ Extra used</button>
                  <button className="btn-ghost text-xs" disabled={!v.boards.some(b => b.netUsed > 0)}
                    onClick={() => setAction({ kind: 'return', boardId: v.boards.find(b => b.netUsed > 0)?.boardId ?? null })}>Return unused</button>
                </div>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
              <Figure label="Issued" value={n(tot.issued)} />
              <Figure label="Extra used" value={tot.extra ? n(tot.extra) : '—'} tone={tot.extra ? 'text-red-600' : undefined} />
              <Figure label="Returned" value={tot.returned ? n(tot.returned) : '—'} tone={tot.returned ? 'text-emerald-700' : undefined} />
              <Figure label="Net used" value={n(tot.netUsed)} />
              <Figure
                label={planned > 0 ? `vs ${n(planned)} planned` : 'Planned (JC)'}
                value={vsPlan == null ? '—' : vsPlan === 0 ? 'On plan' : `${vsPlan > 0 ? 'Over' : 'Under'} ${n(Math.abs(vsPlan))}`}
                tone={vsPlan == null || vsPlan === 0 ? undefined : vsPlan > 0 ? 'text-red-600' : 'text-emerald-700'}
                sub={vsPlan != null && vsPlan !== 0 && planned > 0 ? `${((Math.abs(vsPlan) / planned) * 100).toFixed(1)}%` : undefined}
              />
            </div>
          </div>

          {/* Per board */}
          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Board</th>
                  <th className="px-3 py-2 text-right font-medium">Issued</th>
                  <th className="px-3 py-2 text-right font-medium">Extra used</th>
                  <th className="px-3 py-2 text-right font-medium">Returned</th>
                  <th className="px-3 py-2 text-right font-medium">Net used</th>
                  <th className="px-3 py-2 text-right font-medium">On hand now</th>
                  <th className="px-3 py-2 text-right font-medium">Reconcile</th>
                </tr>
              </thead>
              <tbody>
                {v.boards.length === 0 ? (
                  <tr><td colSpan={7} className="p-4 text-center text-slate-400">No board issued to this job yet.</td></tr>
                ) : v.boards.map(b => (
                  <tr key={b.boardId} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 min-w-[220px]">{boardText(b)}</td>
                    <td className="px-3 py-1.5 text-right">{n(b.issued)}</td>
                    <td className={`px-3 py-1.5 text-right ${b.extra ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{b.extra ? n(b.extra) : '—'}</td>
                    <td className={`px-3 py-1.5 text-right ${b.returned ? 'text-emerald-700 font-semibold' : 'text-slate-400'}`}>{b.returned ? n(b.returned) : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{n(b.netUsed)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-500">{n(b.sheetsOnHand)}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {job && !reconcileOpen && <span className="text-slate-400">Closed</span>}
                      {canReconcile && (
                        <>
                          <button className="text-red-600 hover:underline mr-3 font-medium" onClick={() => setAction({ kind: 'extra', boardId: b.boardId })}>+ Extra used</button>
                          <button className="text-emerald-700 hover:underline font-medium disabled:text-slate-300 disabled:no-underline"
                            disabled={b.netUsed <= 0} onClick={() => setAction({ kind: 'return', boardId: b.boardId })}>Return</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Every movement */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Everything issued to this job</h3>
            <div className="bg-white rounded shadow overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Date</th>
                    <th className="px-3 py-2 text-left font-medium">Board</th>
                    <th className="px-3 py-2 text-left font-medium">What</th>
                    <th className="px-3 py-2 text-right font-medium">Sheets</th>
                    <th className="px-3 py-2 text-left font-medium">Reason</th>
                    <th className="px-3 py-2 text-left font-medium">Notes</th>
                    <th className="px-3 py-2 text-left font-medium">By</th>
                    <th className="px-3 py-2 text-right font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {v.txns.length === 0 ? (
                    <tr><td colSpan={8} className="p-4 text-center text-slate-400">Nothing yet.</td></tr>
                  ) : v.txns.map(t => {
                    const isReversal = t.reversesTxnId != null;
                    const isReversed = t.reversedByTxnId != null;
                    const label = isReversal ? `Reversal of ${TXN_LABEL[t.effectiveType] ?? t.effectiveType}` : (TXN_LABEL[t.type] ?? t.type);
                    // From the job's side: issued and extra are sheets it took; returned is sheets it gave back.
                    const jobSheets = -t.sheets;
                    return (
                      <tr key={t.id} className={`border-t border-slate-100 ${isReversed ? 'text-slate-400 line-through' : isReversal ? 'bg-amber-50/60' : ''}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{when(t.createdOn)}</td>
                        <td className="px-3 py-1.5 min-w-[220px]">{boardText(t)}</td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            isReversal ? 'bg-amber-100 text-amber-800'
                              : t.type === 'RECON_EXTRA' ? 'bg-red-100 text-red-700'
                              : t.type === 'RECON_RETURN' ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-sky-100 text-sky-700'
                          }`}>{label}</span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold">{jobSheets > 0 ? n(jobSheets) : `−${n(-jobSheets)}`}</td>
                        <td className="px-3 py-1.5">{t.reason || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-500 min-w-[200px] max-w-[360px] whitespace-normal">
                          <NoteCell txnId={t.id} notes={t.notes} canAdd={canManage} onSaved={refreshAll} />
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">{t.byName || '—'}</td>
                        <td className="px-3 py-1.5 text-right">
                          {canManage && !isReversal && !isReversed && (t.canReverse ?? true) && (
                            <button className="text-red-600 hover:underline text-[11px] disabled:text-slate-300" disabled={reverse.isPending}
                              title="Undo this movement (both rows stay for the record)"
                              onClick={() => {
                                if (window.confirm(`Reverse this ${label.toLowerCase()} of ${n(Math.abs(t.sheets))} sheets on ${t.sku}? Stock on hand changes back, and both rows stay in History.`)) reverse.mutate(t.id);
                              }}>Reverse</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Sheets are from the job's side: issued and extra used are sheets it took; returned shows as minus.</p>
          </div>
        </div>
      )}

      {action && job && v && (
        <ReconcileModal
          kind={action.kind}
          job={job}
          jobBoards={v.boards}
          boardId={action.boardId}
          onClose={() => setAction(null)}
          onSaved={() => { setAction(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

function Figure({ label, value, tone, sub }: { label: string; value: string; tone?: string; sub?: string }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-2">
      <div className={`text-base font-bold ${tone ?? 'text-slate-800'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      {sub && <div className={`text-[10px] ${tone ?? 'text-slate-500'}`}>{sub}</div>}
    </div>
  );
}

function ReconcileModal({ kind, job, jobBoards, boardId, onClose, onSaved }: {
  kind: Kind; job: JobRef; jobBoards: BoardTotal[]; boardId: number | null;
  onClose: () => void; onSaved: () => void;
}) {
  // Extra sheets can come off any board; a return only goes back to one the job used.
  const allBoards = useQuery({
    queryKey: ['paper-stock', 'all-for-reconcile'],
    queryFn: () => api<{ rows: BoardLite[] }>('/api/paper-stock?sort=sku'),
    enabled: kind === 'extra',
  });
  const choices: { id: number; label: string; onHand: number; netUsed: number }[] = kind === 'extra'
    ? (allBoards.data?.rows ?? []).map(b => ({ id: b.id, label: boardText(b), onHand: b.sheetsOnHand, netUsed: jobBoards.find(j => j.boardId === b.id)?.netUsed ?? 0 }))
    : jobBoards.filter(b => b.netUsed > 0).map(b => ({ id: b.boardId, label: boardText(b), onHand: b.sheetsOnHand, netUsed: b.netUsed }));

  const [board, setBoard] = useState<number | null>(boardId);
  const [sheetsText, setSheetsText] = useState('');
  const reasons = kind === 'extra' ? EXTRA_REASONS : RETURN_REASONS;
  const [reason, setReason] = useState(reasons[0]!);
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const chosen = choices.find(c => c.id === board) ?? null;
  const sheets = readNumber(sheetsText);

  const save = useMutation({
    mutationFn: (body: { sheets: number }) => api(`/api/paper-stock/${board}/job-reconcile`, {
      method: 'POST',
      body: JSON.stringify({ jobId: job.jobId, kind, sheets: body.sheets, reason, notes: notes.trim() || null }),
    }),
    onSuccess: onSaved,
    onError: (e) => {
      const m = (e as Error).message;
      const short = /^insufficient_stock:(\d+):(\d+)$/.exec(m);
      const over = /^return_exceeds_used:(-?\d+):(\d+)$/.exec(m);
      setErr(short ? `Only ${n(Number(short[1]))} sheets of this board are on hand.`
        : over ? `This job has used ${n(Number(over[1]))} sheets of this board — it can't return ${n(Number(over[2]))}.`
        : m === 'job_not_found' ? 'That job card no longer exists.'
        : m === 'job_reconcile_closed' ? `This job's reconcile closed ${RECONCILE_DAYS} days after its first issue — what it left in stock is final.`
        : m);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!board || !chosen) { setErr('Pick the board.'); return; }
    if (sheets == null || Number.isNaN(sheets) || sheets <= 0) { setErr('Enter the sheets — a number or =formula.'); return; }
    if (Math.abs(sheets - Math.round(sheets)) > 1e-6) { setErr(`Sheets must be whole (it comes to ${sheets}).`); return; }
    const q = Math.round(sheets);
    if (kind === 'extra' && q > chosen.onHand) { setErr(`Only ${n(chosen.onHand)} sheets of this board are on hand.`); return; }
    if (kind === 'return' && q > chosen.netUsed) { setErr(`This job has used ${n(chosen.netUsed)} sheets of this board — it can't return ${n(q)}.`); return; }
    if (reason === 'Other' && !notes.trim()) { setErr('Say why in the note.'); return; }
    save.mutate({ sheets: q });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()} onSubmit={onSubmit}>
        <h2 className={`text-lg font-semibold mb-1 ${kind === 'extra' ? 'text-red-700' : 'text-emerald-700'}`}>
          {kind === 'extra' ? 'Extra sheets used' : 'Return unused sheets'}
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          <span className="font-mono font-semibold text-slate-700">{job.jobId}</span>
          {job.productName && <> · {job.productName}</>}
          {' — '}{kind === 'extra' ? 'production used more than was issued; this takes it off stock.' : 'sheets back from the job; this adds them to stock.'}
        </p>
        <div className="grid gap-3">
          <div>
            <label className="label">Board</label>
            <select className="input" value={board ?? ''} onChange={e => setBoard(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— Pick —</option>
              {choices.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            {chosen && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                {kind === 'extra' ? <>On hand: <b>{n(chosen.onHand)}</b> sheets</> : <>This job has used <b>{n(chosen.netUsed)}</b> sheets of it</>}
              </div>
            )}
          </div>
          <div>
            <label className="label">Sheets *</label>
            <input className="input" inputMode="decimal" autoFocus value={sheetsText} onChange={e => setSheetsText(e.target.value)} placeholder="e.g. 150 or =3*50" />
            {sheets != null && Number.isNaN(sheets) && <div className="text-[11px] text-red-600 mt-0.5">Can't read this — use a number or =formula</div>}
            {sheets != null && !Number.isNaN(sheets) && isFormula(sheetsText) && <div className="text-[11px] text-slate-500 mt-0.5">= {n(sheets)}</div>}
          </div>
          <div>
            <label className="label">Reason *</label>
            <select className="input" value={reason} onChange={e => setReason(e.target.value)}>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Note{reason === 'Other' ? ' *' : ''}</label>
            <input className="input" value={notes} onChange={e => setNotes(e.target.value)} maxLength={500}
              placeholder={reason === 'Other' ? 'What happened' : 'Optional — e.g. which machine, who reported it'} />
          </div>
        </div>
        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={save.isPending}
            className={`btn-primary ${kind === 'extra' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
            {save.isPending ? 'Saving…' : kind === 'extra' ? 'Book extra sheets' : 'Return to stock'}
          </button>
        </div>
      </form>
    </div>
  );
}
